/**
 * Members' tasting notes on a wine.
 *
 * `scores.notes` has existed since the first migration and was never shown to a
 * single person. Notes appear on REVEAL; individual numbers stay private until
 * the tasting is final — the live view deliberately never leaks them, and
 * surfacing notes shouldn't quietly reverse that decision.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Avatar } from './Avatar';

interface Note {
  personId: string;
  name: string;
  avatarUrl: string | null;
  notes: string;
  /** Null until the tasting is final. Notes on reveal; scores on final. */
  total: number | null;
}

export function TastingNotes({ eventId, wineId }: { eventId: string; wineId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['notes', eventId, wineId],
    queryFn: () =>
      api
        .get<{ data: { revealed: boolean; notes: Note[] } }>(
          `/events/${eventId}/wines/${wineId}/notes`,
        )
        .then((r) => r.data),
  });

  if (isLoading) return null;
  if (!data?.revealed) return null;
  if (!data.notes.length) {
    return <p className="text-sm text-cream-300/45">No notes on this wine.</p>;
  }

  return (
    <ul className="space-y-3">
      {data.notes.map((n) => (
        <li key={n.personId} className="flex gap-3">
          <Avatar
            firstName={n.name.split(' ')[0] ?? '?'}
            lastName={n.name.split(' ')[1]}
            avatarUrl={n.avatarUrl}
            size="sm"
          />
          <div className="min-w-0 flex-1">
            <p className="flex items-baseline gap-2">
              <span className="text-sm text-cream-100">{n.name}</span>
              {n.total != null ? (
                <span className="text-xs text-gold-400">{n.total}/10</span>
              ) : null}
            </p>
            <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-cream-300/80">
              {n.notes}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}
